/** @jsx jsx */
import { jsx } from '@emotion/react';

import { type MutableRefObject, type ReactNode } from 'react';

import { useVisibilityTracker } from '@coursera/event-pulse/react';

import type { PageConfig_ComponentConfigFragment as PageConfigComponent } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';

type Props = {
  pageConfigComponent: PageConfigComponent;
  children: ReactNode;
  index: number;
  trackableContentRef?: MutableRefObject<HTMLDivElement | null>;
};

const VisibilityTracking = (props: Props) => {
  const { pageConfigComponent, children, index, trackableContentRef } = props;
  const recommender =
    pageConfigComponent.rankingContext || pageConfigComponent.recommenderConfigurationHash
      ? {
          ...(pageConfigComponent.rankingContext && { context: pageConfigComponent.rankingContext }),
          ...(pageConfigComponent.recommenderConfigurationHash && {
            hash: pageConfigComponent.recommenderConfigurationHash,
          }),
        }
      : undefined;

  // Do not track visibility for PageConfig_GroupedComponentsWrapper, but standalone components and the components
  // nested within the group _should_ be tracked.
  const isNonTrackingComponent = pageConfigComponent.__typename === 'PageConfig_GroupedComponentsWrapper';

  const ref: MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_page_config_component', {
    pageConfigComponent: {
      name: pageConfigComponent.__typename,
      id: pageConfigComponent.id,
      index,
      ...(recommender && { recommender }),
    },
  });

  // has a trackableContentRef set but no children
  const isContentEmpty = !!trackableContentRef?.current && !trackableContentRef?.current?.firstElementChild;

  const shouldNotTrack = isNonTrackingComponent || isContentEmpty;

  return <div ref={shouldNotTrack ? null : ref}>{children}</div>;
};

export default VisibilityTracking;
